import { FormEvent, useEffect, useState } from "react";
import { Eye, RefreshCw, Search, Trash2, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminUser,
  ContactMessage,
  ContactMessageStatus,
  PaginationMeta,
  deleteContactMessage,
  getCurrentAdmin,
  listContactMessages,
  logout,
  updateContactMessageStatus,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import Pagination from "./components/Pagination";

const statusLabels: Record<ContactMessageStatus, string> = {
  New: "待處理",
  Replied: "已回覆",
  Closed: "已結案",
};

const statusClass: Record<ContactMessageStatus, string> = {
  New: "bg-[#e9f4ec] text-[#245438]",
  Replied: "bg-[#eef3f8] text-[#255071]",
  Closed: "bg-[#eeeeee] text-[#59635d]",
};

const statusBorderClass: Record<ContactMessageStatus, string> = {
  New: "border-l-[#4f8b61]",
  Replied: "border-l-[#4e7fa3]",
  Closed: "border-l-[#9ba59f]",
};

const statusAccentClass: Record<ContactMessageStatus, string> = {
  New: "text-[#245438]",
  Replied: "text-[#255071]",
  Closed: "text-[#59635d]",
};

const emptyCounts: Record<ContactMessageStatus, number> = {
  New: 0,
  Replied: 0,
  Closed: 0,
};

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

export default function AdminContactMessages() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get("status") || "";
  const [user, setUser] = useState<AdminUser | null>(null);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [counts, setCounts] = useState<Record<ContactMessageStatus, number>>(emptyCounts);
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState(initialStatus);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [viewingMessage, setViewingMessage] = useState<ContactMessage | null>(null);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(filters = { Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setUser(me.User);
      const listData = await listContactMessages(filters);
      setMessages(listData.Messages);
      setCounts(listData.Counts || emptyCounts);
      setPagination(listData.Pagination || defaultPagination);
    } catch (error) {
      if (error instanceof Error) setPageMessage(error.message);
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: initialStatus, Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  async function handleStatusChange(item: ContactMessage, nextStatus: ContactMessageStatus) {
    const previous = messages;
    setMessages((current) => current.map((row) => (row.ContactMessageId === item.ContactMessageId ? { ...row, Status: nextStatus } : row)));
    try {
      await updateContactMessageStatus(item.ContactMessageId, nextStatus);
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setMessages(previous);
      setPageMessage(error instanceof Error ? error.message : "狀態更新失敗，請稍後再試。");
    }
  }

  async function handleDelete(item: ContactMessage) {
    const confirmed = await confirm({
      title: "刪除聯絡訊息？",
      description: (
        <>
          你即將刪除 <strong className="font-bold text-[#203d32]">{item.Name}</strong> 的聯絡訊息。
          <br />
          此操作無法復原。
        </>
      ),
      confirmText: "刪除",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteContactMessage(item.ContactMessageId);
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "刪除失敗，請稍後再試。");
    }
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  return (
    <AdminLayout sectionLabel="內容管理" title="聯絡我們訊息" user={user} onLogout={handleLogout}>
      <div className="mb-4 md:hidden">
        <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button className={`flex min-w-[96px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${status === "" ? "border-[#315746] bg-[#315746] text-white" : "border-[#dfe6e1] bg-white text-[#315746]"}`} onClick={() => { setStatus(""); load({ Status: "", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage }); }} type="button">
            <span className="text-xs font-bold">全部</span>
            <span className="ml-3 text-lg font-bold">{Object.values(counts).reduce((sum, value) => sum + value, 0)}</span>
          </button>

          {(["New", "Replied", "Closed"] as ContactMessageStatus[]).map((value) => (
            <button className={`flex min-w-[108px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${status === value ? "border-[#315746] bg-[#315746] text-white" : "border-[#dfe6e1] bg-white"}`} key={value} onClick={() => { setStatus(value); load({ Status: value, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage }); }} type="button">
              <span className={`text-xs font-bold ${status === value ? "text-white" : statusAccentClass[value]}`}>{statusLabels[value]}</span>
              <span className="ml-3 text-lg font-bold">{counts[value] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-5 hidden gap-3 md:grid md:grid-cols-3">
        {(["New", "Replied", "Closed"] as ContactMessageStatus[]).map((value) => (
          <button
            className={`rounded-md border border-[#dfe6e1] border-l-4 bg-white p-4 text-left transition hover:border-[#b8c8bf] ${statusBorderClass[value]}`}
            key={value}
            onClick={() => {
              setStatus(value);
              load({ Status: value, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
            }}
            type="button"
          >
            <div className={`mb-3 inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusClass[value]}`}>
              {statusLabels[value]}
            </div>
            <div className="text-3xl font-bold text-[#203d32]">{counts[value] || 0}</div>
          </button>
        ))}
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:flex md:flex-wrap md:items-end md:p-4" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="contact-status">狀態</label>
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="contact-status" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[280px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="contact-keyword">關鍵字</label>
          <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="contact-keyword" placeholder="姓名、Email、主題、內容" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] md:w-auto">
          <Search size={18} />
          查詢
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] hover:bg-[#f0f5f2] md:px-5" onClick={() => load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[1080px] border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="px-4 py-3">建立時間</th>
                <th className="px-4 py-3">姓名 / Email</th>
                <th className="px-4 py-3">諮詢主題</th>
                <th className="px-4 py-3">訊息內容</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={6}>載入中...</td></tr>
              ) : messages.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={6}>目前沒有符合條件的聯絡訊息。</td></tr>
              ) : messages.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.ContactMessageId}>
                  <td className="px-4 py-4 align-top text-sm whitespace-nowrap">{item.CreatedAt}</td>
                  <td className="px-4 py-4 align-top">
                    <strong>{item.Name}</strong>
                    <div className="text-sm text-[#59665f]">{item.Email}</div>
                  </td>
                  <td className="px-4 py-4 align-top">{item.Subject}</td>
                  <td className="max-w-[360px] px-4 py-4 align-top text-sm text-[#59665f]">
                    <p className="line-clamp-2 whitespace-pre-wrap">{item.Message}</p>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <select className={`h-10 rounded-md border border-[#cfd8d2] px-3 font-bold outline-none ${statusClass[item.Status]}`} value={item.Status} onChange={(event) => handleStatusChange(item, event.target.value as ContactMessageStatus)}>
                      {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
                        onClick={() => setViewingMessage(item)}
                        type="button"
                      >
                        <Eye size={16} />
                        查看
                      </button>
                      <button
                        className="inline-flex h-9 items-center gap-1 rounded-md border border-[#f0c7c4] bg-white px-3 text-sm font-bold text-[#9a2d2d] transition hover:bg-[#fdebea]"
                        onClick={() => handleDelete(item)}
                        type="button"
                      >
                        <Trash2 size={16} />
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>

      {viewingMessage && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={() => setViewingMessage(null)} type="button" />
          <section className="relative flex max-h-[90vh] w-full max-w-[640px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md">
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-lg font-bold text-[#203d32]">聯絡訊息詳情</h2>
                <p className="mt-1 text-sm text-[#7b877f]">{viewingMessage.CreatedAt}</p>
              </div>
              <button
                aria-label="關閉"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]"
                onClick={() => setViewingMessage(null)}
                type="button"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto px-5 py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">姓名</div>
                  <div className="mt-1 text-sm font-bold text-[#203d32]">{viewingMessage.Name}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">電子郵件</div>
                  <div className="mt-1 text-sm font-bold text-[#203d32]">{viewingMessage.Email}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">諮詢主題</div>
                  <div className="mt-1 text-sm font-bold text-[#203d32]">{viewingMessage.Subject}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">狀態</div>
                  <div className={`mt-1 inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusClass[viewingMessage.Status]}`}>
                    {statusLabels[viewingMessage.Status]}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">訊息內容</div>
                <p className="mt-2 whitespace-pre-wrap rounded-md border border-[#edf0ee] bg-[#f6f7f5] p-4 text-sm leading-6 text-[#3c4a43]">
                  {viewingMessage.Message}
                </p>
              </div>

              {viewingMessage.PageUrl && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">來源頁面</div>
                  <div className="mt-1 break-all text-sm text-[#59665f]">{viewingMessage.PageUrl}</div>
                </div>
              )}
            </div>

            <div className="flex shrink-0 justify-end gap-3 border-t border-[#edf0ee] px-5 py-4">
              <button
                className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
                onClick={() => setViewingMessage(null)}
                type="button"
              >
                關閉
              </button>
            </div>
          </section>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
